import {Alert, Button, Group, Stack, Text, TextInput} from '@mantine/core'
import {IconCalendarExclamation} from '@tabler/icons-react'
import {useState} from 'react'

import {formatJobScheduleRange, toBusinessApiDateString} from '../jobs/job-schedule-format'

type PastEndDatePanelProps = {
    endDate: string
    missingEquipmentCount?: number
    onExtendEndDate: (endDate: string) => Promise<void>
    onMarkCompleted: () => Promise<void>
}

export function PastEndDatePanel({
    endDate,
    missingEquipmentCount: missingEquipment = 0,
    onExtendEndDate,
    onMarkCompleted,
}: PastEndDatePanelProps) {
    const today = toBusinessApiDateString(new Date())
    const [extending, setExtending] = useState(false)
    const [newEndDate, setNewEndDate] = useState(today)
    const [saving, setSaving] = useState<'extend' | 'complete' | null>(null)
    const endLabel = formatJobScheduleRange({startDate: endDate, endDate: null, style: 'compact'})

    async function run(kind: 'extend' | 'complete', action: () => Promise<void>) {
        setSaving(kind)
        try {
            await action()
        } finally {
            setSaving(null)
        }
    }

    return (
        <Alert
            color="orange"
            variant="light"
            icon={<IconCalendarExclamation size={20} />}
            title="Past end date"
            data-testid="past-end-date-panel"
        >
            <Stack gap="xs">
                <Text size="sm">End date was {endLabel}. Extend it or mark the job completed.</Text>
                {extending ? (
                    <Group gap="xs" align="flex-end">
                        <TextInput
                            type="date"
                            label="New end date"
                            min={today}
                            value={newEndDate}
                            onChange={(event) => setNewEndDate(event.currentTarget.value)}
                            data-testid="past-end-date-input"
                        />
                        <Button
                            loading={saving === 'extend'}
                            disabled={!newEndDate || newEndDate < today}
                            onClick={() => void run('extend', () => onExtendEndDate(newEndDate))}
                        >
                            Save end date
                        </Button>
                        <Button variant="subtle" onClick={() => setExtending(false)}>
                            Cancel
                        </Button>
                    </Group>
                ) : (
                    <Group gap="xs">
                        <Button variant="filled" color="orange" onClick={() => setExtending(true)}>
                            Extend end date
                        </Button>
                        <Button
                            variant="default"
                            loading={saving === 'complete'}
                            disabled={missingEquipment > 0}
                            onClick={() => void run('complete', onMarkCompleted)}
                        >
                            Mark completed
                        </Button>
                    </Group>
                )}
                {!extending && missingEquipment > 0 && (
                    <Text size="xs" c="dimmed" data-testid="past-end-date-equipment-note">
                        {missingEquipment} still missing. Enter equipment used first.
                    </Text>
                )}
            </Stack>
        </Alert>
    )
}
